'use client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ClobApiCredentials, ProvisionCredentialsRequest } from '@/types/clob.types';
import { clobApi } from '../api/clob.api';

// ── Query Keys ─────────────────────────────────────────────────────────────
export const CLOB_QUERY_KEYS = {
  all: ['clob'] as const,
  credentialsStatus: () => [...CLOB_QUERY_KEYS.all, 'credentials-status'] as const,
  credentials: () => [...CLOB_QUERY_KEYS.all, 'credentials'] as const,
};

// ── Queries ─────────────────────────────────────────────────────────────────

/**
 * Checks whether CLOB credentials already exist for the current user.
 * Enabled only when the user is authenticated (token present).
 */
export const useClobCredentialsStatus = (enabled = true) => {
  return useQuery({
    queryKey: CLOB_QUERY_KEYS.credentialsStatus(),
    queryFn: () => clobApi.getCredentialsStatus(),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 min – credentials don't change often
    gcTime: 1000 * 60 * 30,
    retry: false,
  });
};

/**
 * Fetches the stored CLOB credentials for the current user.
 * Only call this when you are sure credentials exist.
 */
export const useClobCredentials = (enabled = false) => {
  return useQuery({
    queryKey: CLOB_QUERY_KEYS.credentials(),
    queryFn: () => clobApi.getCredentials(),
    enabled,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
    retry: false,
  });
};

// ── Mutations ────────────────────────────────────────────────────────────────

/**
 * Triggers the backend to provision CLOB API credentials using the
 * Privy builder-signing-sdk. This is the primary mutation that kicks off
 * the Polymarket ClobClient.createApiKey() flow on the server side.
 */
export const useProvisionClobCredentials = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ProvisionCredentialsRequest) => clobApi.provisionCredentials(payload),
    onSuccess: () => {
      // Invalidate status and credentials caches so they refetch with new data
      queryClient.invalidateQueries({ queryKey: CLOB_QUERY_KEYS.all });
    },
  });
};

/**
 * Persists (upserts) CLOB credentials directly on the backend.
 * Used as a fallback if the backend provisioning returns credentials
 * that need to be manually saved.
 */
export const useSetClobCredentials = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: ClobApiCredentials) => clobApi.setCredentials(credentials),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOB_QUERY_KEYS.all });
    },
  });
};

/**
 * Deletes CLOB credentials from the backend.
 * Used when resetting wallet state or re-provisioning.
 */
export const useDeleteClobCredentials = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => clobApi.deleteCredentials(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOB_QUERY_KEYS.all });
    },
  });
};
